import {
  DataTable,
  Dialog,
  FormSectionHeader,
  Heading,
  Loader,
} from "@/components";
import useReports from "./useReports";
import { Button } from "@/components/ui";
import { InvoiceNo } from "./InvoiceNo";
import { InvoiceAmount } from "./InvoiceAmount";
import { InvoiceOverview } from "./InvoiceOverview";
import { InvoiceStatus } from "./InvoiceStatus";
import { PaymentTimeline } from "./PaymentTimeline";
import { DollarSign, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { Filter } from "./Filter";
import { Header } from "./Header";

const Reports = () => {
  const {
    communityDetail,
    breadcrumbData,
    invoiceOverviewCards,
    isReportLoading,
    columns,
    viewPaymentDoc,
    viewReversalDoc,
    showLinkPopup,
    setShowLinkPopup,
    clearingId,
    reversalId,
    paymentCards,
    statusCards,
    setPageNumber,
    setPageSize,
    postedDocumentList,
    isPostedDocLoading,
    isPostedDocRefetching,
    pageSize,
    sorting,
    setSorting,
    pageNumber,
    onTabChange,
    currentTab,
    containerRef,
    indicatorStyle,
    onCancelFilter,
    onFilterBtnClick,
    openFilter,
    filterData,
    setFilterData,
    isMobile,
    onOptionChanges,
    selectedOption,
    onTimelineCardClick
  } = useReports();

  return (
    <div className="p-4 md:p-6">
      {/* Header & Search */}
      <div className="flex flex-col xl:flex-row gap-2 xl:items-center xl:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading
            title={`Invoice Report (Invoice Document Management) - ${
              communityDetail?.name || communityDetail?.communityName
            }`}
            breadcrumbData={breadcrumbData}
          />
          {isMobile && (
            <Header onFilterBtnClick={onFilterBtnClick} option={selectedOption} onOptionChanges={onOptionChanges} />
          )}
        </div>
        {!isMobile && (
          <Header onFilterBtnClick={onFilterBtnClick} option={selectedOption} onOptionChanges={onOptionChanges} />
        )}
      </div>

      {isReportLoading ? (
        <Loader />
      ) : (
        <div className="bg-white mt-[10px] rounded-lg px-[30px] py-[15px]">
          <InvoiceOverview invoiceOverviewCards={invoiceOverviewCards} />
          <InvoiceStatus statusCards={statusCards} />
          <PaymentTimeline paymentCards={paymentCards} onTimelineCardClick={onTimelineCardClick}  />

          {isPostedDocLoading || isPostedDocRefetching ? (
            <Loader />
          ) : (
            <div className="mb-8 rounded-md">
              <FormSectionHeader className="text-[20px] text-primary">
                Reports
              </FormSectionHeader>
              <DataTable
                columns={columns}
                data={postedDocumentList?.data || []}
                pageNumber={pageNumber}
                pageSize={pageSize}
                setPageNumber={setPageNumber}
                setPageSize={setPageSize}
                totalPages={Math.ceil(postedDocumentList?.totalPages)}
                totalRecords={postedDocumentList?.total}
                serverSidePagination={true}
                sorting={sorting}
                onSortingChange={setSorting}
                className="shadow-none rounded-none"
              />
            </div>
          )}
        </div>
      )}
      <div className="bg-white mt-[10px] rounded-lg px-[10px] md:px-[30px] py-[15px]">
        {/* <div className="flex justify-end mb-4">
          <Button onClick={onViewDetail}>
            {showDetails ? "Hide Details" : "View Details"}
          </Button>
        </div> */}

        <div
          ref={containerRef}
          className="relative inline-flex gap-2 border border-gray-300 p-2 md:px-3 rounded-full w-fit"
        >
          {/* Animated background indicator */}
          <div
            className="absolute top-1 bottom-1 rounded-full bg-primary-300 transition-all duration-300 ease-in-out"
            style={indicatorStyle}
          />

          {/* Tab 1 */}
          <div
            data-tab="invoice_number"
            className={cn(
              "relative z-10 flex flex-row gap-2 items-center cursor-pointer p-2 text-[12px] md:text-[14px] rounded-full transition-colors",
              currentTab === "invoice_number" && "text-primary font-medium"
            )}
            onClick={() => onTabChange("invoice_number")}
          >
            <span
              className={cn(
                "p-2 rounded-full transition-colors duration-300",
                currentTab === "invoice_number" && "bg-primary text-white"
              )}
            >
              <FileText size={18} />
            </span>
            Invoice No.
          </div>

          {/* Tab 2 */}
          <div
            data-tab="invoice_amount"
            className={cn(
              "relative z-10 flex flex-row gap-2 items-center cursor-pointer p-2 text-[12px] md:text-[14px] rounded-full transition-colors",
              currentTab === "invoice_amount" && "text-primary font-medium"
            )}
            onClick={() => onTabChange("invoice_amount")}
          >
            <span
              className={cn(
                "p-2 rounded-full transition-colors duration-300",
                currentTab === "invoice_amount" && "bg-primary text-white"
              )}
            >
              <DollarSign size={18} />
            </span>
            Invoice Amount
          </div>
        </div>

        <div className="mt-10">
          {currentTab == "invoice_number" ? (
            <InvoiceNo filterData={filterData} selectedYearOption={selectedOption} />
          ) : (
            <InvoiceAmount filterData={filterData} selectedYearOption={selectedOption} />
          )}
        </div>
      </div>

      {showLinkPopup && (
        <Dialog
          open={showLinkPopup}
          setOpen={setShowLinkPopup}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Linked Document"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          hideFooter
          contentClassName="md:w-[800px]"
        >
          {reversalId || clearingId ? (
            <>
              <div className="text-[14px] font-[400]">
                Select a document below to view its details.
              </div>
              <div className="grid grid-cols-2 gap-6 mt-6">
                {reversalId ? (
                  <Button variant="outline" onClick={viewReversalDoc}>
                    Reverse Document
                  </Button>
                ) : (
                  <></>
                )}
                {clearingId ? (
                  <Button onClick={viewPaymentDoc}>Payment Document</Button>
                ) : (
                  <></>
                )}
              </div>
            </>
          ) : (
            <div>No Linked Document Found</div>
          )}
        </Dialog>
      )}

      {openFilter && (
        <Filter
          onCancel={onCancelFilter}
          filterData={filterData}
          setFilterData={setFilterData}
        />
      )}
    </div>
  );
};

export default Reports;
